import { useEffect, useRef, useState } from "react";
import { authFetchRaw } from "@/lib/api";
import { formatBytes } from "@/lib/format";
import { useAudioPlayer } from "@/stores/audioPlayer";
import {
  describeUnplayableCodec,
  isGenericMime,
  sniffAudio,
} from "./recordingAudioSniff";

type Props = {
  /** Stable id used to coordinate single-active playback across players. */
  recordingId: string;
  /** Endpoint that streams (decrypts) the audio bytes for this recording. */
  downloadUrl: string;
  /** MIME from the DB row, used as a fallback when sniffing fails. */
  mimeType?: string | null;
  /** Display name used as the suggested filename for the inline download link. */
  displayName?: string | null;
  /** Optional file size for diagnostics shown next to codec errors. */
  sizeBytes?: number | null;
  /** Compact rendering for inline mobile contexts where the row already has actions. */
  compact?: boolean;
};

export function RecordingAudioPlayer({
  recordingId,
  downloadUrl,
  mimeType,
  displayName,
  sizeBytes,
  compact = false,
}: Props) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioError, setAudioError] = useState("");
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioMime, setAudioMime] = useState<string | null>(null);
  const [audioBrand, setAudioBrand] = useState<string | null>(null);
  const [audioCodec, setAudioCodec] = useState<string | null>(null);

  const activeId = useAudioPlayer((s) => s.activeId);
  const setActive = useAudioPlayer((s) => s.setActive);

  useEffect(() => {
    let cancelled = false;
    let createdUrl: string | null = null;
    setAudioUrl(null);
    setAudioError("");
    setAudioLoading(true);
    setIsPlaying(false);
    setAudioMime(null);
    setAudioBrand(null);
    setAudioCodec(null);
    (async () => {
      try {
        const resp = await authFetchRaw(downloadUrl);
        if (!resp.ok) {
          throw new Error(`Kon audio niet ophalen (${resp.status}).`);
        }
        const raw = await resp.blob();
        if (cancelled) return;
        const { mime: sniffed, brand, codec } = await sniffAudio(raw);
        const targetMime =
          sniffed
          ?? (!isGenericMime(raw.type) ? raw.type : null)
          ?? (mimeType && !isGenericMime(mimeType) ? mimeType : null)
          ?? "audio/webm";
        const blob = raw.type === targetMime ? raw : new Blob([raw], { type: targetMime });
        createdUrl = URL.createObjectURL(blob);
        setAudioUrl(createdUrl);
        setAudioMime(targetMime);
        setAudioBrand(brand);
        setAudioCodec(codec);
        const unplayable = describeUnplayableCodec(codec);
        if (unplayable) {
          setAudioError(
            `Codec ${unplayable} (${codec}) wordt niet door browsers ondersteund. Gebruik Download en speel het bestand af in bv. VLC.`,
          );
        }
      } catch (err) {
        if (cancelled) return;
        setAudioError(err instanceof Error ? err.message : "Audio laden mislukt.");
      } finally {
        if (!cancelled) setAudioLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [downloadUrl, mimeType]);

  // Pause this player whenever another recording becomes active.
  useEffect(() => {
    if (activeId && activeId !== recordingId) {
      const el = audioRef.current;
      if (el && !el.paused) el.pause();
    }
  }, [activeId, recordingId]);

  // Clear our claim on the global "active" slot when this player unmounts
  // (row collapsed, modal closed). Without this, the next player would
  // pause itself on mount because the store still names us.
  useEffect(() => {
    return () => {
      if (useAudioPlayer.getState().activeId === recordingId) {
        setActive(null);
      }
    };
  }, [recordingId, setActive]);

  const handlePlay = () => {
    const el = audioRef.current;
    if (!el) return;
    el.play().catch((err) => {
      setAudioError(err instanceof Error ? err.message : "Afspelen mislukt.");
    });
  };
  const handlePause = () => {
    audioRef.current?.pause();
  };
  const handleStop = () => {
    const el = audioRef.current;
    if (!el) return;
    el.pause();
    el.currentTime = 0;
  };

  // MediaRecorder-produced WebM blobs lack a duration cue, so the browser
  // reports duration=Infinity and refuses to render a seek bar (and on some
  // builds, refuses to play at all). Seeking past the end forces the
  // demuxer to scan the file, after which duration is correctly populated.
  const handleLoadedMetadata = () => {
    const el = audioRef.current;
    if (!el) return;
    if (Number.isFinite(el.duration) && el.duration > 0) return;
    const onTimeUpdate = () => {
      el.removeEventListener("timeupdate", onTimeUpdate);
      el.currentTime = 0;
    };
    el.addEventListener("timeupdate", onTimeUpdate);
    try {
      el.currentTime = 1e101;
    } catch {
      el.removeEventListener("timeupdate", onTimeUpdate);
    }
  };

  const downloadFilename = (() => {
    const stem = displayName || `recording-${recordingId.slice(0, 8)}`;
    const ext = audioMime ? audioMime.split("/")[1]?.split(";")[0] || "bin" : "bin";
    return `${stem}.${ext}`;
  })();

  return (
    <div className={compact ? "w-full" : "rounded-lg border border-[rgba(10,52,96,0.10)] bg-white p-3"}>
      {!compact ? (
        <div className="mb-2 flex items-center justify-between gap-2">
          <p className="text-xs font-medium text-[var(--cs-navy)]">Afspelen</p>
          {audioUrl ? (
            <a
              href={audioUrl}
              download={downloadFilename}
              className="rounded border border-[rgba(10,52,96,0.10)] bg-white px-2 py-0.5 text-[11px] text-[var(--cs-navy)] hover:bg-[var(--cs-ivory)]"
            >
              Download
            </a>
          ) : null}
        </div>
      ) : null}
      {audioLoading ? (
        <p className="text-xs text-[var(--cs-ink-muted)]">Audio laden…</p>
      ) : audioError ? (
        <div className="space-y-2">
          <p className="text-xs text-red-600">{audioError}</p>
          {!compact ? (
            <p className="text-[10px] text-[var(--cs-ink-muted)]">
              Gedetecteerd: <span className="font-mono">{audioMime ?? "onbekend"}</span>
              {audioBrand ? <> · brand <span className="font-mono">{audioBrand}</span></> : null}
              {audioCodec ? <> · codec <span className="font-mono">{audioCodec}</span></> : null}
              {sizeBytes ? <> · {formatBytes(sizeBytes)}</> : null}
              {mimeType && mimeType !== audioMime ? (
                <> · DB-MIME <span className="font-mono">{mimeType}</span></>
              ) : null}
            </p>
          ) : null}
        </div>
      ) : audioUrl ? (
        <div className={compact ? "" : "space-y-2"}>
          <audio
            ref={audioRef}
            src={audioUrl}
            onLoadedMetadata={handleLoadedMetadata}
            onPlay={() => {
              setIsPlaying(true);
              setActive(recordingId);
            }}
            onPause={() => {
              setIsPlaying(false);
              if (useAudioPlayer.getState().activeId === recordingId) {
                setActive(null);
              }
            }}
            onEnded={() => {
              setIsPlaying(false);
              if (useAudioPlayer.getState().activeId === recordingId) {
                setActive(null);
              }
            }}
            onError={(e) => {
              const mediaErr = (e.currentTarget as HTMLAudioElement).error;
              const codeLabel: Record<number, string> = {
                1: "Afgebroken",
                2: "Netwerkfout",
                3: "Decoderingsfout",
                4: "Niet-ondersteund formaat",
              };
              const label = mediaErr ? codeLabel[mediaErr.code] ?? "Onbekende fout" : "Onbekende fout";
              setAudioError(
                `Audio kan niet afgespeeld worden (${label})${
                  mediaErr?.message ? ` — ${mediaErr.message}` : ""
                }.`,
              );
            }}
            controls
            preload="auto"
            className="w-full"
          />
          {!compact ? (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handlePlay}
                disabled={isPlaying}
                className="rounded-lg border border-[rgba(10,52,96,0.10)] bg-white px-3 py-1 text-xs text-[var(--cs-navy)] hover:bg-[var(--cs-ivory)] disabled:opacity-50"
              >
                Afspelen
              </button>
              <button
                type="button"
                onClick={handlePause}
                disabled={!isPlaying}
                className="rounded-lg border border-[rgba(10,52,96,0.10)] bg-white px-3 py-1 text-xs text-[var(--cs-navy)] hover:bg-[var(--cs-ivory)] disabled:opacity-50"
              >
                Pauzeren
              </button>
              <button
                type="button"
                onClick={handleStop}
                className="rounded-lg border border-[rgba(10,52,96,0.10)] bg-white px-3 py-1 text-xs text-[var(--cs-navy)] hover:bg-[var(--cs-ivory)]"
              >
                Stoppen
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
